import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

export const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL,
  headers: {
    'Content-Type': 'application/ld+json',
    'Accept': 'application/ld+json',
  },
});

// Publieke endpoints (aanmelding, demo-aanvraag, KvK-zoeken) worden zonder
// ingelogde gebruiker aangeroepen. Hier mag GEEN (mogelijk verlopen) token mee,
// en een 401 mag GEEN refresh/redirect naar login triggeren.
const PUBLIC_PATH_PREFIX = '/api/public/';

function isPublicRequest(url?: string): boolean {
  return typeof url === 'string' && url.includes(PUBLIC_PATH_PREFIX);
}

// Automatically attach JWT token and tenant context to every request
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const isPublic = isPublicRequest(config.url);

    const token = localStorage.getItem('accessToken');
    if (token && !isPublic) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // PATCH requests require application/merge-patch+json
    if (config.method === 'patch') {
      config.headers['Content-Type'] = 'application/merge-patch+json';
    }

    // Tenant- en provider-context zijn niet van toepassing op publieke calls.
    if (!isPublic) {
      // Attach active tenant context from selectedRegion
      const storedRegion = localStorage.getItem('selectedRegion');
      if (storedRegion) {
        try {
          const region = JSON.parse(storedRegion);
          if (region?.uuid) {
            config.headers['X-Tenant-Context'] = region.uuid;
          }
        } catch {
          // Invalid JSON in localStorage, skip
        }
      }

      // Attach provider-impersonation context (admin/super admin only)
      const storedProvider = localStorage.getItem('providerContext');
      if (storedProvider) {
        try {
          const provider = JSON.parse(storedProvider);
          if (provider?.uuid) {
            config.headers['X-Provider-Context'] = provider.uuid;
          }
        } catch {
          // Invalid JSON in localStorage, skip
        }
      }
    }
  }
  return config;
});

/**
 * Silent token-refresh op een 401.
 *
 * Zonder dit vervalt de sessie zodra het access-token verloopt terwijl de
 * gebruiker de app gebruikt (de request-interceptor plakt alleen de token,
 * maar verlengt niets). Deze response-interceptor probeert bij een 401 het
 * access-token één keer te vernieuwen via het refresh-token en speelt daarna
 * het oorspronkelijke verzoek opnieuw af.
 *
 * Kenmerken:
 * - Per verzoek maximaal één retry (via de `_retry`-vlag) → geen oneindige loop.
 * - Eén gedeelde refresh-call: parallelle 401's wachten op dezelfde belofte
 *   i.p.v. ieder een eigen refresh te starten.
 * - Faalt de refresh, dan worden de tokens gewist en gaat de gebruiker naar de
 *   loginpagina (consistent met AuthContext.handleSessionExpired).
 */

// Verzoeken die zelf onderdeel van de auth-flow zijn en dus geen refresh-retry
// mogen triggeren.
const AUTH_PATHS = ['/token/refresh', '/login', '/2fa_check'];

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

function clearTokensAndRedirect(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  // Alleen doorsturen als we niet al op de loginpagina staan.
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login?expired=true';
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    // Losse axios-instance zodat deze call niet zelf door de interceptor loopt.
    const { data } = await axios.post<{ token?: string; refresh_token?: string }>(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/token/refresh`,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );

    if (data?.token) {
      localStorage.setItem('accessToken', data.token);
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      return data.token;
    }
    return null;
  } catch {
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriableConfig | undefined;

    if (
      typeof window === 'undefined' ||
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest._retry ||
      isPublicRequest(originalRequest.url) ||
      AUTH_PATHS.some((path) => originalRequest.url?.includes(path))
    ) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // Deel één refresh-call tussen alle gelijktijdige 401's.
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;

    if (!newToken) {
      clearTokensAndRedirect();
      return Promise.reject(error);
    }

    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    return api(originalRequest);
  }
);
